/**
===================================================
Module: frontend.src.components.VideoCropper.tsx
Description: Video cropping component
Owner: Kawada Technosystem Co Ltd
All Rights Reserved
This script is proprietary and confidential.
Unauthorized copying, distribution, or modification of this
file, via any medium, is strictly prohibited.
===================================================
*/

import React, { useState, useRef, useEffect } from "react";
import { VideoPreview } from "./VideoPreview";
import { useSessionCreationStore } from "../stores/sessionCreationStore";
import "../styles/VideoCropper.css";
import { UPLOAD_CONSTRAINTS } from "../services/api";
import { Notification } from "./NotificationSystem";
import NotificationSystem from "./NotificationSystem";

interface VideoCropperProps {
  onCrop: (
    startTime: number,
    endTime: number,
    enableTrimming: boolean,
    duration: number,
  ) => void | Promise<void>;
  onCancel: () => void;
  onBack: () => void;
}

const VideoCropper: React.FC<VideoCropperProps> = ({
  onCrop,
  onCancel,
  onBack,
}) => {
  const { videoFile, thumbnailBlob } = useSessionCreationStore();

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [startTime, setStartTime] = useState(0);
  const [endTime, setEndTime] = useState(0);
  const [enableTrimming, setEnableTrimming] = useState(false);
  const [userEditedTrim, setUserEditedTrim] = useState(false);

  const [inputMode, setInputMode] = useState<"seconds" | "minutes">("seconds");
  const [startTimeInput, setStartTimeInput] = useState("0");
  const [endTimeInput, setEndTimeInput] = useState("0");
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    if (!videoFile) return;
  }, [videoFile]);

  const handleLoadedMetadata = (d: number, videoEl: HTMLVideoElement) => {
    videoRef.current = videoEl;
    const dur = Math.floor(d || 0);
    setDuration(dur);
    if (!userEditedTrim) {
      setStartTime(0);
      setEndTime(dur);
    }
  };

  useEffect(() => {
    if (enableTrimming && duration > 0 && !userEditedTrim) {
      setStartTime(0);
      setEndTime(duration);
    }
  }, [enableTrimming, duration, userEditedTrim]);

  // Update input values when trim values change
  useEffect(() => {
    if (inputMode === "seconds") {
      setStartTimeInput(Math.floor(startTime).toString());
      setEndTimeInput(Math.floor(endTime).toString());
    } else {
      setStartTimeInput(Math.floor(startTime / 60).toString());
      setEndTimeInput(Math.floor(endTime / 60).toString());
    }
  }, [startTime, endTime, inputMode]);

  const toggleInputMode = () => {
    setInputMode((prev) => (prev === "seconds" ? "minutes" : "seconds"));
  };

  const validateAndApplyInputs = () => {
    try {
      let newStartTime = parseFloat(startTimeInput);
      let newEndTime = parseFloat(endTimeInput);

      if (inputMode === "minutes") {
        newStartTime = newStartTime * 60;
        newEndTime = newEndTime * 60;
      }

      // Validate inputs
      if (isNaN(newStartTime) || isNaN(newEndTime)) {
        addNotification("error", "開始時間・終了時間の入力は必須です");
        return;
      }

      if (newStartTime < 0 || newEndTime < 0) {
        addNotification("error", "開始時間・終了時間は０以上を入力してください");
        return;
      }

      if (newStartTime >= newEndTime) {
        addNotification("error", "開始時間が終了時間を超えないようにしてください");
        return;
      }

      if (newEndTime > duration) {
        addNotification("error", "終了時間が動画の再生時間を超えないようにしてください");
        return;
      }

      // Check duration constraint
      const trimmedDuration = newEndTime - newStartTime;
      if (trimmedDuration > UPLOAD_CONSTRAINTS.MAX_DURATION_SECONDS) {
        addNotification(
          "error",
          `トリミング後の動画の長さが${UPLOAD_CONSTRAINTS.MAX_DURATION_SECONDS / 60} 分を超えないようにしてください`,
        );
        return;
      }

      // Apply the values
      setStartTime(Math.floor(newStartTime));
      setEndTime(Math.floor(newEndTime));
      addNotification("success", "トリミングが完了しました");
    } catch (error) {
      addNotification("error", "トリミングに失敗しました");
    }
  };

  const addNotification = (type: "success" | "error", message: string) => {
    const newNotification: Notification = {
      id: Date.now().toString(),
      type,
      message,
      duration: 5000,
    };
    setNotifications((prev) => [...prev, newNotification]);
  };

  const removeNotification = (id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  };

  if (!videoFile) {
    return (
      <div className="video-cropper-container">
        <div className="error-message">
          No video file found. Please go back and select a video.
        </div>
        <button onClick={onBack}>Go Back</button>
      </div>
    );
  }

  const handlePlayPause = async () => {
    if (!videoRef.current) return;
    if (isPlaying) {
      videoRef.current.pause();
    } else {
      try {
        await videoRef.current.play();
      } catch {}
    }
    setIsPlaying(!isPlaying);
  };

  const handleStartTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Only allow integers for both seconds and minutes
    const intValue = value.replace(/[^0-9]/g, "");
    setStartTimeInput(intValue);
  };

  const handleEndTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Only allow integers for both seconds and minutes
    const intValue = value.replace(/[^0-9]/g, "");
    setEndTimeInput(intValue);
  };

  const handleStartTimeIncrement = () => {
    setUserEditedTrim(true);
    const newStartTime = Math.min(startTime + 1, Math.max(endTime - 1, 0));
    setStartTime(newStartTime);
    if (videoRef.current && currentTime < newStartTime) {
      videoRef.current.currentTime = newStartTime;
      setCurrentTime(newStartTime);
    }
    if (newStartTime >= endTime) {
      setEndTime(Math.min(newStartTime + 1, duration || newStartTime + 1));
    }
  };

  const handleStartTimeDecrement = () => {
    setUserEditedTrim(true);
    const newStartTime = Math.max(startTime - 1, 0);
    setStartTime(newStartTime);
  };

  const handleEndTimeIncrement = () => {
    setUserEditedTrim(true);
    const newEndTime = Math.min(endTime + 1, duration || endTime + 1);
    setEndTime(newEndTime);
  };

  const handleEndTimeDecrement = () => {
    setUserEditedTrim(true);
    const newEndTime = Math.max(endTime - 1, startTime + 1);
    setEndTime(newEndTime);
    if (videoRef.current && currentTime > newEndTime) {
      videoRef.current.currentTime = newEndTime;
      setCurrentTime(newEndTime);
    }
  };

  const handleTrimVideo = async () => {
    if (!thumbnailBlob) return;

    let durationToCheck: number;
    let durationType: string;

    if (enableTrimming) {
      // Check the trimmed portion duration
      durationToCheck = endTime - startTime;
      durationType = "trimmed";
    } else {
      // Check the full video duration
      durationToCheck = duration;
      durationType = "full";
    }

    // Check if duration exceeds limit
    if (durationToCheck > UPLOAD_CONSTRAINTS.MAX_DURATION_SECONDS) {
      const durationMinutes = Math.round((durationToCheck / 60) * 100) / 100;
      addNotification(
        "error",
        `動画の長さが${UPLOAD_CONSTRAINTS.MAX_DURATION_SECONDS / 60}分を超えないようにしてください`,
      );
      return;
    }

    // Duration is valid, proceed with processing
    onCrop(startTime, endTime, enableTrimming, durationToCheck);
  };

  const handleClose = () => {
    setTimeout(() => {
      onCancel();
    }, 300);
  };

  const handleBack = () => {
    setTimeout(() => {
      onBack();
    }, 300);
  };

  return (
    <>
      <NotificationSystem
        notifications={notifications}
        onRemove={removeNotification}
      />

      <div className="video-cropper-overlay" onClick={handleClose}>
        <div
          className="video-cropper-modal"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="video-cropper-header">
            <h2 className="modal-title">トリミング</h2>
          </div>

          <div className="video-cropper-body">
            <div className="video-section">
              <div className="video-player-container">
                <VideoPreview
                  ref={videoRef}
                  file={videoFile}
                  startTime={enableTrimming ? startTime : 0}
                  endTime={enableTrimming ? endTime : undefined}
                  playing={isPlaying}
                  loop={false}
                  onLoadedMetadata={(d, el) => {
                    handleLoadedMetadata(d, el);
                  }}
                  onTimeUpdate={(t) => setCurrentTime(Math.floor(t))}
                  controls
                  className="video-player"
                />
              </div>
            </div>

            <div
              className={`trimming-section ${enableTrimming ? "trimming-enabled" : ""}`}
            >
              <div className="trim-controls">
                <div className="enable-trimming-section">
                  <input
                    type="checkbox"
                    checked={enableTrimming}
                    onChange={(e) => setEnableTrimming(e.target.checked)}
                    disabled={duration === 0}
                  />
                  <span className="enable-trimming-text">トリミングする</span>
                </div>

                <div className="time-controls">
                  <div className="time-inputs-row">
                    <div className="time-control-group">
                      <label className="time-label">開始時間</label>
                      <div className="input-with-unit">
                        <input
                          type="number"
                          className="time-input"
                          step={1}
                          value={startTimeInput}
                          onChange={handleStartTimeChange}
                          disabled={duration === 0}
                          placeholder="0"
                        />
                        <span className="input-unit">
                          {inputMode === "seconds" ? "秒" : "分"}
                        </span>
                      </div>
                    </div>

                    <div className="time-control-group">
                      <label className="time-label">終了時間</label>
                      <div className="input-with-unit">
                        <input
                          type="number"
                          className="time-input"
                          step={1}
                          value={endTimeInput}
                          onChange={handleEndTimeChange}
                          disabled={duration === 0}
                          placeholder="0"
                        />
                        <span className="input-unit">
                          {inputMode === "seconds" ? "秒" : "分"}
                        </span>
                      </div>
                    </div>

                    <div className="time-control-group">
                      <label className="time-label">単位</label>
                      <button
                        type="button"
                        className="time-mode-btn"
                        onClick={toggleInputMode}
                        title={`${inputMode === "seconds" ? "分" : "秒"}に切り替え`}
                      >
                        {inputMode === "seconds" ? "秒" : "分"}
                      </button>
                    </div>

                    <button
                      type="button"
                      className="time-validate-btn"
                      onClick={validateAndApplyInputs}
                      title="入力の検証と適用"
                    >
                      適用
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="video-cropper-actions">
              <button className="btn-secondary" onClick={handleClose}>
                キャンセル
              </button>
              <button className="btn-secondary" onClick={handleBack}>
                戻る
              </button>
              <button
                className="btn-primary"
                onClick={handleTrimVideo}
                disabled={!thumbnailBlob}
              >
                処理を開始
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default VideoCropper;
